// schedule-summary.tsx — the compose dialog's "what will happen" strip.
//
// WHY THIS EXISTS
// The dialog answers four questions — who, what, when, and what happens when I
// press Send — and it answered them in four separate places and never assembled
// them. The recipient count lived only as a row of chips at the top; the fan-out
// rule ("multiple recipients are scheduled individually") was helper text buried
// under the picker; the schedule was a bare grey sentence under the time row;
// and the past-time warning was inside the desktop-only WhatsApp preview panel,
// so on a phone it did not exist at all.
//
// This strip is that assembly, placed where the decision is actually made —
// directly above the Send button.
//
// THE "WHEN" LINE IS NOW THE DATE/TIME CONTROL.
// The Schedule-time pickers used to sit under the Options tab, hidden behind a
// tab yet required for every send, so people could not find where to set the
// time and Send stayed disabled. Those pickers moved OUT of Options: the date
// and the time in this sentence are the input now. Each is a real <button> that
// opens its existing picker (calendar / time list), styled as an accented,
// underlined inline word so it reads as a control, not prose. It writes the same
// `dueDate` anchor the pickers always did — the sole date/time input for both
// one-off and recurring drafts. For a recurring draft the cadence ("Repeats
// every month on day 25") is shown as a quiet second line below the anchor.

import React from 'react';
import { Clock, Radio, Repeat, Users } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import moment from 'moment';

import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectScheduleRecipients } from '@common-reducers/AddScheduledMessageButtonReducer';
import { hasStatusRecipient } from '@common/scheduling/status-recipient';
import { namespace } from '@common/config/constants';
import { formatInLanguage, getMomentLocale } from '@common/config/moment-locales';
import { useClockPreference } from '@common/hooks/use-clock-preference';
import { formatClockTime } from '@common/config/time-format';
import { getRuleOptionsSafe } from '@common/components/schedule-picker/recurrence-utils';
import { recurrenceCadenceSentence } from '@common/i18n/recurrence-cadence';
import ScheduleDatePicker from '@common/components/schedule-picker/schedule-date-picker';
import ScheduleTimePicker from '@common/components/schedule-picker/schedule-time-picker';

import styles from './compose-dialog.module.scss';
import { resolvePastTimeState } from './past-time-gate';
import { useNowTicker } from './use-now-ticker';

// The interactive tokens read the LIVE editor state (ScheduledMessageEditorReducer)
// that the pickers write and the dialog reads — NOT the debounced messageDraft
// mirror — so the word updates the instant a date/time is chosen.
const selectEditorDueDate = (state: any) => state.ScheduledMessageEditorReducer.dueDate;

/**
 * The date word in the sentence. The label ("Friday, September 25, 2026") is
 * injected by <Trans> as `children` — so word order is the locale's, not ours —
 * and rendered as an accented, underlined button that opens the calendar. `warn`
 * paints it amber when the chosen time is past, so the eye lands on the control
 * that fixes the footer's block.
 */
const InteractiveDate: React.FC<{ warn?: boolean; children?: React.ReactNode }> = ({ warn, children }) => {
    const { t } = useTranslation(namespace);
    return (
        <ScheduleDatePicker
            trigger={
                <button
                    type="button"
                    className={`${styles.scheduleToken}${warn ? ` ${styles.scheduleTokenWarn}` : ''}`}
                    aria-label={t('messageScheduling.summary.changeDate', 'Change date')}
                >
                    {children}
                </button>
            }
        />
    );
};

/**
 * The time word in the sentence. Same contract as InteractiveDate; opens the
 * time list. The label ("10:03 AM" / a 24-hour clock in Hebrew) arrives as
 * `children` from <Trans>.
 */
const InteractiveTime: React.FC<{ warn?: boolean; children?: React.ReactNode }> = ({ warn, children }) => {
    const { t } = useTranslation(namespace);
    return (
        <ScheduleTimePicker
            trigger={
                <button
                    type="button"
                    className={`${styles.scheduleToken}${warn ? ` ${styles.scheduleTokenWarn}` : ''}`}
                    aria-label={t('messageScheduling.summary.changeTime', 'Change time')}
                >
                    {children}
                </button>
            }
        />
    );
};

/** `plain` — no strip surface: the sentence sits inline at the head of the
 *  dialog's options group (tall layout) instead of as a boxed strip. */
export const ScheduleSummary: React.FC<{ plain?: boolean; actions?: React.ReactNode }> = ({ plain = false, actions }) => {
    const { t, i18n } = useTranslation(namespace);
    const clockPreference = useClockPreference();
    const recipients = useAppSelector(selectScheduleRecipients);
    const isStatus = hasStatusRecipient(recipients);
    const count = recipients.length;

    // LIVE editor state — the same source the pickers and the dialog use, so the
    // sentence, the warn mark and the tokens can never disagree with each other.
    const dueDateRaw = useAppSelector(selectEditorDueDate);
    // BE#657 — treat a date the user has NOT explicitly picked (a seeded ~now value on
    // a fresh draft) as "no date chosen yet", so the summary shows the "pick a date /
    // pick a time" prompts and never advertises a send time the user did not choose.
    // BE#657 M3 — OR the editor flag with the DRAFT flag. On a fresh pick the editor
    // flag flips instantly (live summary); on EDITING AN EXISTING message the editor
    // load resets its flag to false, but the draft flag is true (editMessage sets it
    // from `_id && dueDate`), so without the draft fallback the summary wrongly showed
    // "pick a date / pick a time" while Send was enabled (summary↔button disagreement).
    const editorDueDateSetByUser = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.dueDateSetByUser);
    const draftDueDateSetByUser = useAppSelector((state: any) => state.AddScheduledMessageButtonReducer?.messageDraft?.dueDateSetByUser);
    const dueDateSetByUser = !!(editorDueDateSetByUser || draftDueDateSetByUser);
    const dueDate = dueDateSetByUser ? dueDateRaw : '';
    const isRecurring = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.isRecurring);
    const rruleset = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.rruleset);
    const isPoll = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.isPoll);

    // Same predicate + inputs as the footer's gate, so the mark and the block
    // cannot disagree. The sentence stays; only its mark + the time word change
    // colour — the footer callout directly below carries the words and the remedy.
    const now = useNowTicker();
    const { warn: timeHasPassed } = resolvePastTimeState({
        dueDate,
        isRecurring,
        rruleset,
        now,
    });

    // The words the two tokens carry — formatted against the render's OWN
    // language (not moment's global locale, which lags languageChanged), exactly
    // as generateRecurrenceText's non-interactive sentence did: weekday-long date
    // + the locale's own time. Empty-state fallbacks are near-unreachable (the
    // pickers seed dueDate to "now" on mount) but kept honest.
    const dateLabel = dueDate
        ? formatInLanguage(moment.utc(dueDate).local(), i18n.language, 'dddd, LL')
        : t('messageScheduling.summary.pickDate', 'pick a date');
    const timeLabel = dueDate
        ? formatClockTime(moment.utc(dueDate).local(), i18n.language, clockPreference)
        : t('messageScheduling.summary.pickTime', 'pick a time');

    // The recurring cadence, in the shared grammar the calendar hover card and
    // the campaigns summary already use ("every month on day 25"). Day names come
    // from the render's OWN language, not moment's global locale (which lags the
    // i18next languageChanged event). '' for a rule the grammar declines — then
    // no second line is shown.
    const cadence = React.useMemo(() => {
        if (!isRecurring) return '';
        const options = getRuleOptionsSafe(rruleset, dueDate);
        const weekdayNames = moment().locale(getMomentLocale(i18n.language)).localeData().weekdays();
        return recurrenceCadenceSentence(options, weekdayNames, t as (k: string, o?: any) => string);
    }, [isRecurring, rruleset, dueDate, i18n.language, t]);

    // WHO — and, when there is more than one, the fan-out rule stated as a
    // consequence ("3 messages") rather than as a footnote about how we store
    // things. A person about to press Send wants the number of messages that
    // will leave their account.
    // No line at all while nobody is chosen: the footer's "Please add at least
    // one recipient" note already says it, next to the button it blocks.
    let audienceLine: string | null;
    if (isStatus) {
        audienceLine = t('messageScheduling.summary.statusRecipient', 'Posted to your Status');
    } else if (count === 0) {
        audienceLine = null;
    } else if (count === 1) {
        const only = recipients[0] as { displayName?: string; name?: string };
        audienceLine = t('messageScheduling.summary.oneRecipient', 'One message to {{name}}', {
            name: only?.displayName || only?.name || '',
        });
    } else {
        audienceLine = t(
            'messageScheduling.summary.manyRecipients',
            '{{count}} messages — one to each recipient',
            { count },
        );
    }

    const AudienceIcon = isStatus ? Radio : Users;

    return (
        <div className={styles.root}>
            <div className={plain ? styles.summaryPlain : styles.summary}>
                {audienceLine && (
                    <div className={styles.summaryLine}>
                        <span className={styles.summaryMark} aria-hidden="true">
                            <AudienceIcon size={15} />
                        </span>
                        <span className={styles.summaryStrong}>{audienceLine}</span>
                    </div>
                )}

                {/* WHEN — the anchor. The date + the time are the schedule input
                    now (the picker block left the Options tab). <Trans> keeps the
                    words in each locale's order (RTL-safe) and drops the two
                    clickable tokens into place; the tokens format their own label
                    and open their own picker. */}
                <div className={styles.summaryLine}>
                    <span
                        className={`${styles.summaryMark} ${timeHasPassed ? styles.summaryMarkWarn : ''}`}
                        aria-hidden="true"
                    >
                        <Clock size={15} />
                    </span>
                    <span>
                        <Trans
                            t={t}
                            i18nKey={isPoll
                                ? 'messageScheduling.willBeSentOnInteractivePoll'
                                : 'messageScheduling.willBeSentOnInteractive'}
                            defaults={isPoll
                                ? 'This poll will be sent on <date>{{date}}</date> at <time>{{time}}</time>'
                                : 'This message will be sent on <date>{{date}}</date> at <time>{{time}}</time>'}
                            values={{ date: dateLabel, time: timeLabel }}
                            components={{
                                date: <InteractiveDate warn={timeHasPassed} />,
                                time: <InteractiveTime warn={timeHasPassed} />,
                            }}
                        />
                    </span>
                    {/* Compact layout: the options, as icons, at the line's end. */}
                    {actions && <span className={styles.summaryActions}>{actions}</span>}
                </div>

                {/* Recurring only: the cadence, quiet, as context under the anchor. */}
                {cadence && (
                    <div className={`${styles.summaryLine} ${styles.cadenceLine}`}>
                        <span className={styles.cadenceMark} aria-hidden="true">
                            <Repeat size={14} />
                        </span>
                        <span>{t('messageScheduling.summary.repeatsCadence', 'Repeats {{cadence}}', { cadence })}</span>
                    </div>
                )}

                {/* THE PAST-TIME LINE LIVES IN THE FOOTER NOW.
                    It moved because it changed from a warning into a BLOCK: Send
                    is disabled while the chosen time is gone (past-time-gate.ts),
                    and the footer is where this dialog already states, inline,
                    every reason Send is off. The time word above turns amber to
                    point at the control that fixes it. */}
            </div>
        </div>
    );
};

export default ScheduleSummary;
